import { css } from '@linaria/core'
import type { TFunction } from 'i18next'
import { type FC, useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '#app/store'
import type { RecoverableConfiguration, TruckConfiguration } from '#app/store/slices/configurationSlice'
import { getConfigurationTitle, getMarketDenomination } from '#features/Configuration/ConfigurationTitle'
import { ConfigurationStatusIcon } from '#pages/Configuration/components/ConfigurationStatusIcon'
import { configurationLeadingType, configurationStatus, isTruckConfiguration } from '#pages/Configuration/components/configurationStatus'
import { getAllVariants } from '#pages/Configuration/utils/collections'
import { fragments, style } from '#styles/index'
import { stringToDate, stringToPeriod } from '#utils/DateString'
import { useCurrencyFormatter } from '#utils/formatCurrency'
import { formatDuration } from '#utils/formatDuration'

const configurationHeaderHoverClass = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  border: 1px solid ${style.theme.borderSurface};
  background: ${style.theme.backgroundSurfacePrimary};
  color: ${style.theme.textDefault};

  > .info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    overflow: hidden;

    > .title {
      display: flex;
      flex-direction: column;
      max-width: 200px;
      gap: 8px;

      > .type {
        display: flex;
        gap: 2px;
        color: ${style.theme.textSoft};
      }

      > .denomination {
        display: flex;
        flex-direction: column;

        > .name {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          ${fragments.textStyle.heading.s.regular}
        }

        > .market-denomination {
          ${fragments.textStyle.heading.xs.regular}
        }
      }

      > .delivery-date {
        display: flex;
        flex-direction: column;
        color: ${style.theme.textSoft};
        ${fragments.textStyle.body.m.regular};
      }
    }

    > .visualization {
      min-width: 95px;
      flex-shrink: 0;
      height: 74px;
      width: 95px;
      object-fit: cover;
    }
  }

  > .status {
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    gap: 4px;

    > .man-code {
      display: flex;
      justify-content: flex-end;
      color: ${style.theme.textSoft};
      ${fragments.textStyle.heading.xs.regular};
    }

    > .pricing {
      display: flex;
      justify-content: flex-end;
      ${fragments.textStyle.heading.s.regular}
    }

    > .editing {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      justify-content: space-between;
      color: ${style.theme.textSoft};
      ${fragments.textStyle.body.xs.regular}
    }
  }
`

const makeStatusTranslation = (status: ReturnType<typeof configurationStatus>, t: TFunction): string => {
  switch (status) {
    case 'saved':
      return t('configuration.status.saved', 'Saved')
    case 'unsaved':
      return t('configuration.status.unsaved', 'Unsaved')
    case 'saving':
      return t('configuration.status.saving', 'Loading')
  }
}

const makeTypeTranslation = (type: ReturnType<typeof configurationLeadingType>, t: TFunction): string => {
  switch (type) {
    case 'leading':
      return t('configuration.status.leading', 'Leading')

    case 'alternative':
    case 'read-only':
      return t('configuration.status.alternative', 'Variation')
  }
}

export const ConfigurationHeaderHover: FC<{ configuration: TruckConfiguration | RecoverableConfiguration }> = ({ configuration }) => {
  const { t } = useTranslation()
  const leadingConfiguration = useAppSelector((x) => x.truckConfigurations.leadingConfiguration)
  const type = configurationLeadingType(configuration, leadingConfiguration)
  const status = configurationStatus(configuration)
  const configurationTitle = getConfigurationTitle(configuration.title, configuration.marketDenomination, t)
  const currency = useCurrencyFormatter()
  const price = isTruckConfiguration(configuration)
    ? getAllVariants(configuration.productGroups)
        .filter((x) => !!x.isSelected)
        .filter((x) => x !== undefined)
        .reduce((a, b) => a + (b.dummy_price ?? 0), 0)
    : undefined

  // update the component 30 seconds to keep the timer reasonably updated
  const rerender = useReducer(() => {
    return {}
  }, {})[1]
  useEffect(() => {
    const renderTask = setInterval(() => {
      rerender()
    }, 30_000)
    return () => clearInterval(renderTask)
  }, [])
  return (
    <div className={configurationHeaderHoverClass}>
      <div className="info">
        <div className="title">
          <div className="type">
            <ConfigurationStatusIcon configuration={configuration} />
            <span className="name">{makeTypeTranslation(type, t)}</span>
          </div>
          <div className="denomination">
            <h1 className="name">{configurationTitle}</h1>
            <h2 className="market-denomination">{getMarketDenomination(configuration.marketDenomination, t)}</h2>
          </div>
          <div className="delivery-date">
            {configuration.apiConfiguration.desiredDealerDate && (
              <span>
                {t('setup.delivery-date.label')} {stringToDate(configuration.apiConfiguration.desiredDealerDate).toLocaleDateString()}
              </span>
            )}
            {configuration.apiConfiguration.partPeriod && (
              <span>
                {t('setup.part-period.label')} {stringToPeriod(configuration.apiConfiguration.partPeriod)}
              </span>
            )}
          </div>
        </div>
        <img className="visualization" src="/placeholders/sample_truck_side-front.png" />
      </div>
      <div className="status">
        {configuration.apiConfiguration.manCode && <span className="man-code">{configuration.apiConfiguration.manCode}</span>}

        <span className="pricing">{price ? currency.formatCurrency(price) : undefined}</span>
        <div className="editing">
          <span className="status">{makeStatusTranslation(status, t)}</span>
          <span className="edit">
            {t('configuration.header.last-edit', {
              time: formatDuration(Date.now() - configuration.lastApiInteraction, t, {
                allowMilliseconds: false,
                allowSeconds: false,
                relative: true,
              }),
            })}
          </span>
        </div>
      </div>
    </div>
  )
}
